
/**
 * Note: Keep this type in sync with the list of environment variables
 * defined under mimirEnv in clients/vuetify/vite.config.ts
 */
type CrossContextEnvironmentVariableName = 'NODE_ENV' | 'VUE_APP_AWS_API_PREFIX' | 'VITE_I18NEXT_DEBUG';

// Minimal declaration for TypeScript compilation in non-DOM contexts
declare const mimirEnv: Record<string, string | undefined> | undefined;

/**
 * Only use this function in contexts where you're not sure whether you're
 * running in a browser environment or in Node.js that still needs CommonJS
 * compatibility.
 *
 * In Node.js use: process.env[name]
 *
 * In a browser use: import.meta.env[name]
 *
 * Ideally, any call to this function should be replaced with code that injects
 * the value from higher up in the call stack where the exact nature of the
 * execution environment is known.
 *
 * Note: In browser contexts, this function only works for variables that have
 * been explicitly listed in clients/vuetify/vite.config.ts
 *
 * Normally code would be checking import.meta.env instead, but this syntax
 * causes compile errors in old CommonJS contexts that haven't yet been migrated
 * to ES modules.
 */
export function getVariableFromContextEnvironment(name: CrossContextEnvironmentVariableName): string | undefined {
  if (typeof mimirEnv !== 'undefined') {
    /*
      Cannot use import.meta.env here because we haven't fully migrated to ESM,
      and this code needs be parsable in both browser contexts and CommonJS
      Node.js contexts.
    */
    return mimirEnv?.[name];
  }

  if (typeof process !== 'undefined') {
    return process.env[name];
  }

  return undefined;
}
